import { Dispatch, SetStateAction, useCallback, useRef } from 'react';

import { AsyncResp, isAbortError } from '#/api/fetch-api';

import { logException } from '#/features/logging/logging';

import { useInterval } from './useInterval';

export interface PollingConnectionBaseState {
  readonly status: 'loading' | 'refetching' | 'error' | 'connected';
  readonly error: string | null;
}

interface Options<
  EntityResp,
  EntityLiveConnectionState extends PollingConnectionBaseState,
> {
  setState: Dispatch<SetStateAction<EntityLiveConnectionState>>;
  isReady?: boolean;
  fetchAll: (signal: AbortSignal) => AsyncResp<EntityResp>;
  setAll: (data: EntityResp) => void;
  resetAll: () => void;
  refetchInterval: number;
}

export default function usePollingConnection<
  EntityResp,
  EntityLiveConnectionState extends PollingConnectionBaseState,
>(options: Options<EntityResp, EntityLiveConnectionState>): void {
  const {
    setState,
    isReady = true,
    fetchAll,
    setAll,
    resetAll,
    refetchInterval,
  } = options;

  const isInitialFetch = useRef(true);

  const fetchIfReady = useCallback(() => {
    if (!isReady) {
      return;
    }

    const controller = new AbortController();
    const cleanUp = () => {
      isInitialFetch.current = true;
      controller.abort();
    };
    const sync = async (signal: AbortSignal): Promise<void> => {
      setLoading();

      const fetchResp = await fetchAll(signal);
      if (signal.aborted) return;
      if (!fetchResp.ok) {
        const message = 'Failed to fetch data';
        const error = new Error(message, { cause: fetchResp.error });
        throw error;
      }
      setAll(fetchResp.data);

      setConnected();
    };

    sync(controller.signal).catch((cause: Error) => {
      if (isAbortError(cause)) return;

      const message = 'Could not load data. Retrying…';
      const error = new Error(message, { cause });
      setFailed(message);
      logException(error);
    });

    return () => {
      cleanUp();
      resetAll();
    };

    function setLoading() {
      const status = isInitialFetch.current ? 'loading' : 'refetching';
      setState((state) => ({ ...state, status, error: '' }));
    }

    function setConnected() {
      isInitialFetch.current = false;
      setState((state) => ({ ...state, status: 'connected', error: '' }));
    }

    function setFailed(error: string) {
      cleanUp();
      resetAll();
      setState((state) => ({ ...state, status: 'error', error }));
    }
  }, [setState, isReady, resetAll, fetchAll, setAll]);

  useInterval(fetchIfReady, isReady ? refetchInterval : null, true);
}
